import React, { useState } from 'react';
import { palettePresets, PaletteConfig } from '../config/palette-presets';
import PaletteDemo from './PaletteDemo';

const PaletteShowcase: React.FC = () => {
  const [selectedPalette, setSelectedPalette] = useState<string>('cyan');
  const [copied, setCopied] = useState<boolean>(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const currentConfig = palettePresets[selectedPalette];

  const copyConfig = async (config?: PaletteConfig, key?: string) => {
    const targetConfig = config || currentConfig;
    const configJson = JSON.stringify(targetConfig, null, 2);
    try {
      await navigator.clipboard.writeText(configJson);
      setCopied(true);
      if (key) {
        setCopiedKey(key);
      }
      setTimeout(() => {
        setCopied(false);
        setCopiedKey(null);
      }, 2000);
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold mb-4 glow-text text-accent-cyan">
          配色方案展示
        </h2>
        <p className="text-text-secondary text-lg">
          切换不同配色方案，查看实际应用效果
        </p>
      </div>

      <div className="glass-effect rounded-xl p-6 border border-border-default mb-8">
        <h3 className="text-xl font-semibold mb-4 text-text-primary">选择配色方案</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {Object.entries(palettePresets).map(([key, preset]) => (
            <div
              key={key}
              className={`relative p-4 rounded-lg border-2 transition-all duration-200 ${
                selectedPalette === key ? 'scale-105' : ''
              }`}
              style={{
                backgroundColor: selectedPalette === key ? preset.background.card : preset.background.dark,
                borderColor: selectedPalette === key ? preset.border.glow : preset.border.default,
                boxShadow: selectedPalette === key ? `0 0 20px ${preset.glow}` : 'none',
              }}
            >
              <button
                onClick={() => setSelectedPalette(key)}
                className="w-full text-left cursor-pointer"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className="w-8 h-8 rounded-lg"
                    style={{
                      backgroundColor: preset.accent,
                      boxShadow: `0 0 10px ${preset.glow}`,
                    }}
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-sm" style={{ color: preset.text.primary }}>
                      {preset.name}
                    </div>
                    <div className="text-xs" style={{ color: preset.text.muted }}>
                      {key}
                    </div>
                  </div>
                </div>
                <p className="text-xs mt-2" style={{ color: preset.text.secondary }}>
                  {preset.description}
                </p>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  copyConfig(preset, key);
                }}
                className="absolute top-2 right-2 p-1.5 rounded transition-all duration-200 cursor-pointer"
                style={{
                  backgroundColor: `${preset.accent}20`,
                  color: preset.accent,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${preset.accent}30`;
                  e.currentTarget.style.boxShadow = `0 0 8px ${preset.glow}`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = `${preset.accent}20`;
                  e.currentTarget.style.boxShadow = 'none';
                }}
                title="复制配置"
              >
                {copiedKey === key ? (
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div
              className="w-4 h-4 rounded-full"
              style={{
                backgroundColor: currentConfig.accent,
                boxShadow: `0 0 10px ${currentConfig.glow}`,
              }}
            />
            <h3 className="text-2xl font-bold" style={{ color: currentConfig.text.primary }}>
              当前方案：{currentConfig.name}
            </h3>
          </div>
          <button
            onClick={() => copyConfig()}
            className="px-4 py-2 rounded-lg font-medium transition-all duration-200 cursor-pointer flex items-center gap-2"
            style={{
              backgroundColor: `${currentConfig.accent}20`,
              color: currentConfig.accent,
              border: `1px solid ${currentConfig.border.default}`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = currentConfig.border.hover;
              e.currentTarget.style.boxShadow = `0 0 15px ${currentConfig.glow}`;
              e.currentTarget.style.backgroundColor = `${currentConfig.accent}30`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = currentConfig.border.default;
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.backgroundColor = `${currentConfig.accent}20`;
            }}
          >
            {copied ? (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                已复制
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                复制配置
              </>
            )}
          </button>
        </div>
        <p className="text-text-secondary mb-6">{currentConfig.description}</p>
      </div>

      <PaletteDemo config={currentConfig} />

      <div className="glass-effect rounded-xl p-6 border border-border-default mt-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-text-primary">配色值</h3>
          <button
            onClick={() => copyConfig()}
            className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer flex items-center gap-2"
            style={{
              backgroundColor: `${currentConfig.accent}15`,
              color: currentConfig.accent,
              border: `1px solid ${currentConfig.border.default}`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = currentConfig.border.hover;
              e.currentTarget.style.backgroundColor = `${currentConfig.accent}25`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = currentConfig.border.default;
              e.currentTarget.style.backgroundColor = `${currentConfig.accent}15`;
            }}
          >
            {copied ? (
              <>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                已复制
              </>
            ) : (
              <>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                复制JSON
              </>
            )}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <div className="text-sm mb-2 text-text-secondary">主色调</div>
            <div className="flex items-center gap-2">
              <div
                className="w-12 h-12 rounded-lg border border-border-default"
                style={{ backgroundColor: currentConfig.primary }}
              />
              <code className="text-xs font-mono text-text-muted">{currentConfig.primary}</code>
            </div>
          </div>
          <div>
            <div className="text-sm mb-2 text-text-secondary">强调色</div>
            <div className="flex items-center gap-2">
              <div
                className="w-12 h-12 rounded-lg border border-border-default"
                style={{ backgroundColor: currentConfig.accent }}
              />
              <code className="text-xs font-mono text-text-muted">{currentConfig.accent}</code>
            </div>
          </div>
          <div>
            <div className="text-sm mb-2 text-text-secondary">背景色</div>
            <div className="flex items-center gap-2">
              <div
                className="w-12 h-12 rounded-lg border border-border-default"
                style={{ backgroundColor: currentConfig.background.card }}
              />
              <code className="text-xs font-mono text-text-muted">{currentConfig.background.card}</code>
            </div>
          </div>
          <div>
            <div className="text-sm mb-2 text-text-secondary">文本色</div>
            <div className="flex items-center gap-2">
              <div
                className="w-12 h-12 rounded-lg border border-border-default"
                style={{ backgroundColor: currentConfig.text.primary }}
              />
              <code className="text-xs font-mono text-text-muted">{currentConfig.text.primary}</code>
            </div>
          </div>
        </div>
        <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: currentConfig.background.darker }}>
          <div className="text-sm mb-2 text-text-secondary">完整配置 JSON</div>
          <pre className="text-xs font-mono overflow-x-auto" style={{ color: currentConfig.text.muted }}>
            {JSON.stringify(currentConfig, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default PaletteShowcase;
